import { BehaviorSubject, Observable, filter, map, merge, share, switchMap, tap } from 'rxjs';
import { Pub, State, Sub } from '../../../../../../shared/state/state.abstract';
import { OverviewOrdersRes, overviewService } from '../../../../services/overview.service';
import { overviewState } from '../../../states/overview.state';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler';
import { dataHelper } from '../../../../../../shared/helpers/data/data.helper';
import { storageHelper } from '../../../../../../shared/helpers/storage';
import { GridSortModel } from '@mui/x-data-grid-premium';

export const initOrderOverviewState: IOrdersOverviewState = {
  action: undefined,
  orderOverview: [],
  isActiveGroupMode: false,
  search: '',
  sortModel: [{ field: 'orderNo', sort: 'asc' }],
};

class PubImpl extends Pub<IOrdersOverviewState> {
  init(params: Partial<IOrdersOverviewState>) {
    this.emit('init', params);
  }
  toggleGroupMode() {
    this.emit('toggleGroupMode', {});
  }
  search(search: string) {
    this.emit('search', { search });
  }
  sort(sortModel: IOrdersOverviewState['sortModel']) {
    storageHelper.local.setItem('overview.orderSort', sortModel);
    this.emit('sort', { sortModel });
  }
}

class SubImpl extends Sub<IOrdersOverviewState> {
  private loading$ = new BehaviorSubject<boolean>(false);
  private shareLoading$: Observable<boolean> = this.loading$.pipe(share());
  protected actionHandlers(): Observable<IOrdersOverviewState> {
    return merge(this.getData(), this.toggleGroupMode(), this.updateOrdersOverview()).pipe(
      map((state) => {
        const { search, orderOverview, sortModel } = state;
        let updatedOrderOverview = [...(orderOverview || [])] as OverviewOrdersRes;

        updatedOrderOverview = dataHelper
          .data(orderOverview as [])
          .sort({ sortModel })
          .search({
            search,
            fields: [['orderDate'], 'customerNr', 'internalOrFullName', 'orderNo'],
          })
          .result() as OverviewOrdersRes;

        this.stream$.next({ ...state, action: 'internalUpdate' });
        return { ...state, orderOverview: updatedOrderOverview };
      }),
    );
  }

  loading(): Observable<boolean> {
    return this.shareLoading$;
  }

  private updateOrdersOverview(): Observable<IOrdersOverviewState> {
    return this.actionListener(['search', 'sort']);
  }

  private getData(): Observable<IOrdersOverviewState> {
    return overviewState.sub.state().pipe(
      filter(
        ({ action }) => action === 'updateDatePeriod' || action === 'updateDeliverynoteGroupId',
      ),
      tap(() => this.loading$.next(true)),
      switchMap(({ datePeriod, deliveryNoteGroupId }) => {
        overviewService.pub.getOverviewOrders({
          filter: {
            periodStart: datePeriod?.fromDate,
            periodEnd: datePeriod?.toDate,
            deliveryNoteGroupId,
          },
        });

        return overviewService.sub.getOverviewOrders().pipe(
          responseHandler<OverviewOrdersRes>({
            customErrorHandler: () => 'order.tab_not_loaded',
            errorReturnType: [],
          }),
          map((data) => {
            const state = this.stream$.getValue();
            this.loading$.next(false);
            return {
              ...state,
              action: 'getData',
              orderOverview: data,
            } as IOrdersOverviewState;
          }),
        );
      }),
    );
  }

  private toggleGroupMode(): Observable<IOrdersOverviewState> {
    return this.actionListener('toggleGroupMode').pipe(
      map((oldState) => {
        const state = structuredClone(oldState);
        const oldStatus = state?.isActiveGroupMode;
        const newStatus = !oldStatus;
        storageHelper.local.setItem('overview.orderIsGroupModeActive', newStatus);
        state.isActiveGroupMode = newStatus;
        return state;
      }),
    );
  }
}

class OrderOverviewState extends State<IOrdersOverviewState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const orderOverviewState = new OrderOverviewState(initOrderOverviewState);

export const getInitSortModel = (): GridSortModel => {
  const initSortModel =
    storageHelper.local.getItem('overview.orderSort') || initOrderOverviewState?.sortModel;
  return initSortModel;
};

export interface IOrdersOverviewState {
  action: 'init' | 'internalUpdate' | 'getData' | 'toggleGroupMode' | 'search' | 'sort' | undefined;
  orderOverview: OverviewOrdersRes;
  isActiveGroupMode: boolean;
  search: string;
  sortModel: GridSortModel;
}
